import { MessageBubble } from './MessageRenderer';
import { Bot, User, AlertCircle, Download, Check, Square, FileText, File as FilePdf, FileCode, FileSpreadsheet, CheckSquare } from 'lucide-react';
import { Message } from './MessageRenderer';
import { useState, useEffect } from 'react';
import { BusinessReportManager } from './reports/BusinessReportManager';

interface Props {
  messages: Message[];
  messagesEndRef: React.RefObject<HTMLDivElement>;
  conversationTitle?: string;
}

export function ChatMessages({ messages, messagesEndRef, conversationTitle }: Props) {
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [showReportManager, setShowReportManager] = useState(false);
  const [reportContent, setReportContent] = useState('');

  // Check if all selectable messages are selected
  const areAllMessagesSelected = () => {
    const selectableMessages = messages.filter(m => m.role !== 'system' && m.role !== 'error');
    return selectableMessages.length > 0 && selectedMessages.size === selectableMessages.length;
  };

  // Toggle selection of a single message
  const toggleMessageSelection = (messageId: string) => {
    setSelectedMessages(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  // Toggle selection of all messages
  const toggleAllMessages = () => {
    if (areAllMessagesSelected()) {
      // If all are selected, deselect all
      setSelectedMessages(new Set());
    } else {
      // Otherwise, select all selectable messages
      const selectableMessageIds = messages
        .filter(m => m.role !== 'system' && m.role !== 'error')
        .map(m => m.id || `temp-${messages.indexOf(m)}-${m.created_at}`);
      setSelectedMessages(new Set(selectableMessageIds));
    }
  };

  // Show report manager when messages are selected
  useEffect(() => {
    if (selectedMessages.size > 0) {
      // Get content from selected messages
      const selectedContent = Array.from(selectedMessages)
        .map(id => {
          const message = messages.find(m => (m.id || `temp-${messages.indexOf(m)}-${m.created_at}`) === id);
          return message ? `${message.role === 'user' ? 'Usuario' : 'Asistente'}: ${message.content}` : '';
        })
        .filter(Boolean)
        .join('\n\n');
      
      if (selectedContent) {
        setReportContent(selectedContent);
        setShowReportManager(true);
      }
    }
  }, [selectedMessages, messages]);

  const downloadMessageAsText = (message: Message) => {
    try {
      // Create a blob with the message content
      const blob = new Blob([message.content], { type: 'text/plain' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      
      // Set the file name - use timestamp to make it unique
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const role = message.role === 'user' ? 'usuario' : 'asistente';
      a.download = `mensaje-${role}-${timestamp}.txt`;
      
      // Append to the document, click, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Release the URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading message:', error);
      alert('Error al descargar el mensaje');
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-white shadow-sm border-x border-gray-200">
      {/* Report Generation Button - Only show when messages are selected */}
      {selectedMessages.size > 0 && (
        <div className="sticky top-0 z-10 mb-4 bg-blue-50 p-3 rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium text-blue-700">
            {selectedMessages.size} {selectedMessages.size === 1 ? 'mensaje seleccionado' : 'mensajes seleccionados'}
          </span>
          <button
            onClick={() => {
              const selectedContent = Array.from(selectedMessages)
                .map(id => {
                  const message = messages.find(m => (m.id || `temp-${messages.indexOf(m)}-${m.created_at}`) === id);
                  return message ? `${message.role === 'user' ? 'Usuario' : 'Asistente'}: ${message.content}` : '';
                })
                .filter(Boolean)
                .join('\n\n');
              
              if (selectedContent) {
                setReportContent(selectedContent);
                setShowReportManager(true);
              }
            }}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center"
          >
            <FileText className="w-4 h-4 mr-1" />
            Generar Informe
          </button>
        </div>
      )}

      <div className="space-y-6">
        {messages.map((message, index) => {
          // Generate a stable ID for the message if it doesn't have one
          const messageId = message.id || `temp-${index}-${message.created_at}`;
          
          return (
            <div
              key={messageId}
              className={`flex ${
                message.role === 'system' 
                  ? 'justify-center' 
                  : message.role === 'error'
                  ? 'justify-start'
                  : message.role === 'user'
                  ? 'justify-end'
                  : 'justify-start'
              }`}
            >
              {message.role === 'system' ? (
                <div className="bg-gray-100 px-4 py-2 rounded-full text-sm text-gray-600 shadow-sm">
                  {message.content}
                </div>
              ) : (
                <div
                  className={`flex items-start space-x-2 max-w-[80%] ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                      message.role === 'user'
                        ? 'bg-blue-500'
                        : message.role === 'error'
                        ? 'bg-red-500'
                        : 'bg-gray-200'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="w-5 h-5 text-white" />
                    ) : message.role === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-gray-700" />
                    )}
                  </div>
                  
                  <div className="space-y-1 relative group">
                    <MessageBubble message={message} />
                    
                    {message.file_ids?.length > 0 && (
                      <div className="mt-1 space-y-1 text-sm">
                        {message.file_ids.map((fileId) => (
                          <a
                            key={fileId}
                            href={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-file?file_id=${fileId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            📎 Descargar archivo ({fileId})
                          </a>
                        ))}
                      </div>
                    )}
                    
                    {/* Message actions - appear on hover */}
                    {message.role !== 'error' && (
                      <div className={`absolute right-0 top-0 mt-1 mr-1 flex space-x-1 transition-opacity ${
                        selectedMessages.size > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        <button
                          onClick={() => toggleMessageSelection(messageId)}
                          className={`p-1 rounded ${
                            selectedMessages.has(messageId) ? 'bg-blue-100 text-blue-500' : 'bg-gray-100 text-gray-400'
                          } hover:bg-blue-50 hover:text-blue-500 transition-colors`}
                          title={selectedMessages.has(messageId) ? "Deseleccionar" : "Seleccionar"}
                        >
                          {selectedMessages.has(messageId) ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => downloadMessageAsText(message)}
                          className="p-1 rounded bg-gray-100 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-colors"
                          title="Descargar como texto"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay mensajes aún</p>
            <p className="text-sm">Selecciona un asistente para comenzar</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Report Manager Modal */}
      {showReportManager && (
        <BusinessReportManager
          content={reportContent}
          onClose={() => {
            setShowReportManager(false);
            setSelectedMessages(new Set());
          }}
          conversationTitle={conversationTitle}
        />
      )}
    </div>
  );
}